import type { DataConnection, Peer } from 'peerjs';

/** PeerJS is only fetched when someone hosts or joins — solo players never download it. */
const loadPeer = (): Promise<typeof Peer> => import('peerjs').then((m) => m.Peer);

/** Unambiguous code alphabet (no I/L/O/0/1): 31⁶ ≈ 890M rooms. */
const ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
/** Namespaces our peer ids on the public PeerJS broker. */
const PREFIX = 'farther-room-v2-';
const CONNECT_TIMEOUT = 12000;

export function newRoomCode(): string {
  let s = '';
  for (let i = 0; i < 6; i++) s += ALPHABET[Math.floor(Math.random() * ALPHABET.length)];
  return s;
}

export function normalizeRoomCode(code: string): string {
  return code.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
}

export interface RoomEvents {
  /** Hosting: the code is registered and shareable. */
  onHosting(code: string): void;
  /** Both peers are connected. */
  onConnected(): void;
  onMessage(msg: unknown): void;
  /** Host only: the friend left; the room stays open for someone else. */
  onFriendLeft(): void;
  /** The room is gone (friend left, connection lost, or it never opened). */
  onClosed(reason: string): void;
}

/**
 * A two-player room over WebRTC data channels (PeerJS). The host registers a short code as its
 * peer id on the public PeerJS broker; the guest connects to it. After the handshake all traffic
 * is peer to peer. Also measures round-trip time for latency compensation.
 */
export class Room {
  readonly isHost: boolean;
  code: string;
  /** Smoothed round-trip time (ms). */
  rtt = 120;
  private peer: Peer | null = null;
  private conn: DataConnection | null = null;
  private pingTimer = 0;
  private openTimer = 0;
  private closed = false;
  private tries = 0;

  private constructor(isHost: boolean, code: string, private readonly events: RoomEvents) {
    this.isHost = isHost;
    this.code = code;
  }

  static host(events: RoomEvents): Room {
    const r = new Room(true, newRoomCode(), events);
    void r.openHost();
    return r;
  }

  static join(code: string, events: RoomEvents): Room {
    const r = new Room(false, normalizeRoomCode(code), events);
    void r.openGuest();
    return r;
  }

  private get pretty(): string {
    return `${this.code.slice(0, 3)}-${this.code.slice(3)}`;
  }

  get connected(): boolean {
    return !!this.conn?.open;
  }

  send(msg: object): void {
    if (this.conn?.open) this.conn.send(msg);
  }

  leave(): void {
    this.shutdown();
  }

  private async openHost(): Promise<void> {
    let PeerCtor: typeof Peer;
    try {
      PeerCtor = await loadPeer();
    } catch {
      this.fail("Can't load multiplayer — check your connection");
      return;
    }
    if (this.closed) return;
    const peer = new PeerCtor(PREFIX + this.code);
    this.peer = peer;
    peer.on('open', () => this.events.onHosting(this.code));
    peer.on('connection', (conn) => {
      if (this.conn?.open) {
        // Two's company: turn away a third.
        conn.on('open', () => {
          conn.send({ k: 'full' });
          setTimeout(() => conn.close(), 200);
        });
        return;
      }
      this.adopt(conn);
    });
    peer.on('error', (err) => {
      if (err.type === 'unavailable-id' && this.tries++ < 5) {
        // Someone already has this code: roll another.
        peer.destroy();
        this.code = newRoomCode();
        void this.openHost();
        return;
      }
      if (!this.conn?.open) this.fail(err.type === 'network' || err.type === 'server-error' ? "Can't reach the room server" : 'Could not open a room');
    });
    peer.on('disconnected', () => {
      // Lost the broker; the data channel can live on without it. Try to stay findable.
      if (!this.closed && !peer.destroyed) peer.reconnect();
    });
  }

  private async openGuest(): Promise<void> {
    this.openTimer = window.setTimeout(() => {
      if (!this.conn?.open) this.fail(`Couldn't reach room ${this.pretty}`);
    }, CONNECT_TIMEOUT);
    let PeerCtor: typeof Peer;
    try {
      PeerCtor = await loadPeer();
    } catch {
      this.fail("Can't load multiplayer — check your connection");
      return;
    }
    if (this.closed) return;
    const peer = new PeerCtor();
    this.peer = peer;
    peer.on('open', () => {
      const conn = peer.connect(PREFIX + this.code, { reliable: true, serialization: 'json' });
      this.adopt(conn);
    });
    peer.on('error', (err) => {
      if (this.conn?.open) return;
      this.fail(err.type === 'peer-unavailable' ? `No room called ${this.pretty}` : err.type === 'network' || err.type === 'server-error' ? "Can't reach the room server" : 'Could not join');
    });
  }

  private adopt(conn: DataConnection): void {
    this.conn = conn;
    conn.on('open', () => {
      clearTimeout(this.openTimer);
      this.events.onConnected();
      this.pingTimer = window.setInterval(() => this.send({ k: 'ping', t: performance.now() }), 1000);
    });
    conn.on('data', (data) => {
      const msg = data as { k?: string; t?: number };
      if (msg.k === 'ping') this.send({ k: 'pong', t: msg.t });
      else if (msg.k === 'pong' && typeof msg.t === 'number') this.rtt += (performance.now() - msg.t - this.rtt) * 0.25;
      else if (msg.k === 'full') this.fail(`Room ${this.pretty} is full`);
      else this.events.onMessage(msg);
    });
    conn.on('close', () => {
      if (this.conn !== conn || this.closed) return;
      if (this.isHost) {
        this.conn = null;
        clearInterval(this.pingTimer);
        this.events.onFriendLeft();
      } else this.fail('The host left');
    });
    conn.on('error', () => {
      if (this.conn === conn && !conn.open) this.fail('Connection lost');
    });
  }

  private fail(reason: string): void {
    if (this.closed) return;
    this.shutdown();
    this.events.onClosed(reason);
  }

  private shutdown(): void {
    this.closed = true;
    clearInterval(this.pingTimer);
    clearTimeout(this.openTimer);
    try {
      this.conn?.close();
    } catch {
      /* already closed */
    }
    this.peer?.destroy();
    this.conn = null;
    this.peer = null;
  }
}
